import { useCallback, useEffect, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import { useTranslation } from 'react-i18next';
import EditorFrame, { EditorView, EditorViewTitle } from './EditorFrame';
import TuningJobStatus from './TuningJobStatus';
import { IconCog } from '../Icons';
import { useFreshnessCheck } from '../../hooks/useFreshnessCheck';
import { useOrganizeJob } from '../../hooks/useOrganizeJob';
import { useVersionJob } from '../../hooks/useVersionJob';
import { remember, remembered } from '../../utils/browserStorage';

const OpenKey = 'entire-editor:open';
const WidthKey = 'entire-editor:width';
// The player page keeps this width free in the game area's right padding, so the tab never covers the game.
export const TuningTabWidth = 33;
const TabHeight = 45;
const DefaultWidth = 380;
const MinWidth = 280;
const MaxWidth = 720;
const NoticeMs = 6000;

interface TuningEditorProps {
  projectId: string;
  versionId: string;
  buildUrl: string;
  /** The panel opens and closes by the gear tab; the scenes view keeps the area open and hides the gear. */
  view: EditorView;
}

export default function TuningEditor({ projectId, versionId, buildUrl, view }: TuningEditorProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(() => remembered(OpenKey) === 'true');
  const shown = view === 'scenes' || open;
  const [width, setWidth] = useState(() => clampWidth(Number(remembered(WidthKey))));
  const [resizing, setResizing] = useState(false);
  const [notice, showNotice] = useNotice();
  const versionJob = useVersionJob(projectId, versionId);
  const organizeJob = useOrganizeJob(projectId);
  useFreshnessCheck(() => {
    versionJob.recheck();
    organizeJob.recheck();
  });
  const arrangedVersionId = organizeJob.state.phase === 'arranged' ? organizeJob.state.versionId : null;

  const toggle = () => {
    setOpen(!open);
    remember(OpenKey, String(!open));
  };

  const startResize = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      event.preventDefault();
      const startX = event.clientX;
      const startWidth = width;
      let latest = startWidth;
      setResizing(true);
      const move = (moved: PointerEvent) => {
        latest = clampWidth(startWidth + startX - moved.clientX);
        setWidth(latest);
      };
      const stop = () => {
        setResizing(false);
        remember(WidthKey, String(latest));
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', stop);
      };
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', stop);
    },
    [width],
  );

  return (
    <div style={{ position: 'relative', flexShrink: 0, display: 'flex' }}>
      {shown && (
        <aside
          style={{
            width,
            borderLeft: '1px solid var(--line)',
            background: 'var(--bg-2)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <div
            onPointerDown={startResize}
            style={{ position: 'absolute', insetBlock: 0, left: 0, width: 6, cursor: 'col-resize', zIndex: 1 }}
          />

          <div
            style={{
              padding: '10px 12px',
              borderBottom: '1px solid var(--line)',
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
            }}
          >
            <div style={{ fontSize: 12, color: 'var(--text)' }}>{t(EditorViewTitle[view])}</div>
            <TuningJobStatus projectId={projectId} versionJob={versionJob} organizeJob={organizeJob} />
          </div>

          {notice && (
            <div style={{ padding: '8px 12px', fontSize: 11, color: 'var(--warn)', borderBottom: '1px solid var(--line)' }}>
              {notice}
            </div>
          )}

          <EditorFrame
            key={view}
            projectId={projectId}
            versionId={versionId}
            buildUrl={buildUrl}
            view={view}
            interactive={!resizing}
            jobRunning={versionJob.running}
            arrangedVersionId={arrangedVersionId}
            showNotice={showNotice}
            onVersionJobStarted={versionJob.started}
            onOrganizeStarted={organizeJob.queued}
          />
        </aside>
      )}

      {view === 'panel' && (
        <button
          type="button"
          onClick={toggle}
          title={t('editor.title')}
          aria-label={t('editor.title')}
          aria-expanded={open}
          style={{
            position: 'absolute',
            top: 4,
            right: open ? 'calc(100% - 1px)' : '100%',
            zIndex: 2,
            width: TuningTabWidth,
            height: TabHeight,
            padding: 0,
            display: 'grid',
            placeItems: 'center',
            background: 'var(--bg-2)',
            border: '1px solid var(--line)',
            borderRight: 'none',
            borderRadius: '12px 0 0 12px',
            color: open ? 'var(--accent)' : 'var(--text-2)',
            cursor: 'pointer',
          }}
        >
          <IconCog size={21} />
        </button>
      )}
    </div>
  );
}

function useNotice(): [string | null, (text: string) => void] {
  const [text, setText] = useState<string | null>(null);
  const timer = useRef<number | undefined>(undefined);

  const show = useCallback((next: string) => {
    setText(next);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setText(null), NoticeMs);
  }, []);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  return [text, show];
}

const clampWidth = (value: number) =>
  Number.isFinite(value) && value > 0 ? Math.min(MaxWidth, Math.max(MinWidth, value)) : DefaultWidth;
